import type { Dictionary } from "@bdo/cornerstone-ui";
import { dynamicApiSlice } from "../dynamicApi";
import type { ODataResponse, WorkflowDefinition } from "@/types";
import { buildODataQuery } from "@/helpers";
import type { WorkflowInstance } from "@/types/WorkflowInstance";
import type { RichInputComponentDefinition } from "@/types/RichInputComponentDefinition";

export interface Workflow {
  id: string;
  workflowInstanceName: string;
  workflowDefinition: WorkflowDefinition;
  status:
    | "InProgress"
    | "Completed"
    | "Cancelled"
    | "Archived"
    | "RequiresAttention";
  modifiedOn: string;
}

export type WorkflowList = Workflow[];

export interface AccessControlEntry {
  email: string;
  name: string;
  isNotificationEnabled: boolean;
  isOwner: boolean;
}

export interface AddUsersRequest {
  workflowInstanceId: string;
  userEmails: string[];
}

export interface RemoveUsersRequest {
  workflowInstanceId: string;
  userEmails: string[];
}

export interface ToggleNotificationsRequest {
  workflowInstanceId: string;
  userEmail: string;
}

export interface SearchValidUsersRequest {
  workflowInstanceId: string;
  searchUsers?: string;
}

export interface ValidUser {
  email: string;
  name: string;
  displayName?: string;
}

export const workflowInstancesApi = dynamicApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getWorkflows: builder.query<
      WorkflowList,
      {
        filter?: string;
        select?: string[];
        expand?: string;
        top?: number;
        skip?: number;
      }
    >({
      query: (params) => `WorkflowInstances?${buildODataQuery(params)}`,
      transformResponse: (response: ODataResponse<WorkflowList>) =>
        response.value,
    }),
    getWorkflowInstanceById: builder.query<
      WorkflowInstance,
      { id: string; select?: string[]; expand?: string }
    >({
      query: ({ id, select, expand }) =>
        `WorkflowInstances(${id})?${buildODataQuery({ select, expand })}`,
      providesTags: (_result, _err, arg) => [
        { type: "WorkflowInstance", id: arg.id },
        { type: "WorkflowInstance", id: arg.id.toLowerCase() },
      ],
    }),
    getWorkflowInstanceInputs: builder.query<
      RichInputComponentDefinition[],
      { id: string }
    >({
      query: ({ id }) => `WorkflowInstances/${id}/inputs`,
      transformResponse: (response: RichInputComponentDefinition[]) => response,
    }),
    createWorkflowInstance: builder.mutation<
      ODataResponse<string>,
      {
        workflowDefinitionId: string;
        name: string;
        inputValues: Dictionary<string | number>;
      }
    >({
      query: ({ workflowDefinitionId, name, inputValues }) => ({
        url: `WorkflowInstances`,
        method: "POST",
        body: {
          workflowDefinitionId,
          name,
          inputValuesJson: JSON.stringify(inputValues),
        },
      }),
    }),
    getAccessControlEntries: builder.query<
      AccessControlEntry[],
      { workflowInstanceId: string }
    >({
      query: ({ workflowInstanceId }) =>
        `WorkflowInstances/${workflowInstanceId}/AccessControlEntries`,
      providesTags: (_result, _err, arg) => [
        { type: "AccessControlEntry", id: arg.workflowInstanceId },
      ],
    }),
    searchValidUsers: builder.mutation<ValidUser[], SearchValidUsersRequest>({
      query: ({ workflowInstanceId, searchUsers }) => ({
        url: `WorkflowInstances/SearchValidUsers`,
        method: "POST",
        body: {
          workflowInstanceId,
          searchUsers,
        },
      }),
    }),
    addUsers: builder.mutation<void, AddUsersRequest>({
      query: ({ workflowInstanceId, userEmails }) => ({
        url: `WorkflowInstances/AddUsers`,
        method: "POST",
        body: {
          workflowInstanceId,
          userEmails,
        },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "AccessControlEntry", id: arg.workflowInstanceId },
      ],
    }),
    removeUsers: builder.mutation<void, RemoveUsersRequest>({
      query: ({ workflowInstanceId, userEmails }) => ({
        url: `WorkflowInstances/RemoveUsers`,
        method: "POST",
        body: {
          workflowInstanceId,
          userEmails,
        },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "AccessControlEntry", id: arg.workflowInstanceId },
      ],
    }),
    subscribeNotifications: builder.mutation<void, ToggleNotificationsRequest>({
      query: ({ workflowInstanceId, userEmail }) => ({
        url: `WorkflowInstances/SubscribeNotifications`,
        method: "POST",
        body: {
          workflowInstanceId,
          userEmail,
        },
      }),
    }),
    unsubscribeNotifications: builder.mutation<
      void,
      ToggleNotificationsRequest
    >({
      query: ({ workflowInstanceId, userEmail }) => ({
        url: `WorkflowInstances/UnsubscribeNotifications`,
        method: "POST",
        body: {
          workflowInstanceId,
          userEmail,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateWorkflowInstanceMutation,
  useGetWorkflowInstanceByIdQuery,
  useGetWorkflowInstanceInputsQuery,
  useGetAccessControlEntriesQuery,
  useSearchValidUsersMutation,
  useAddUsersMutation,
  useRemoveUsersMutation,
  useSubscribeNotificationsMutation,
  useUnsubscribeNotificationsMutation,
  usePrefetch,
} = workflowInstancesApi;
